import { MetadataKeys } from "ez-node-base";
import { MetadataValue } from "@grpc/grpc-js";

import { FrontMeta, GetProducts, GetFrontModules } from "services/page/app";
import { TModuleType, TModule, TProductModule } from "services/page/admin";
import { ModuleInteraction } from "services/page/common";
import { transformMetaForApi } from ".";
import { isUAT } from "utils/debug";
import { transformSign } from "utils/md5";
import { heightLightTabIndex, ParseProductTagFromUrlResp } from "./url";
import { RuntimeProps } from "../../views/index";


const LOAD_LIMIT = 20;
export interface ModuleComponentsProps {
	id: string;
	positionIndex?: number;
	className?: string;
	style?: React.CSSProperties;
}

interface PoolForProductResp {
	moduleId: string;
	pool: TProductModule;
}

export const isLazyMod = (type: TModuleType) => {
	return [TModuleType.TMagicVoucher, TModuleType.TMultiProductModuleType].includes(type);
};

/**
 * 取有效module
 */
export const getValidModules = (mods: TModule[]): TModule[] => {
	return mods.filter((mod: TModule) => Object.keys(mod.module).length > 0);
};
/**
 * 取有效module的除@type之外的content
 */
export const getValidModuleData = (mod: TModule): { [key: string]: any } => {
	const dataKeys = Object.keys(mod.module).filter((item) => item !== "@type");
	return dataKeys.reduce((result, item) => {
		return {
			...result,
			[item]: mod.module[item],
		};
	}, {});
};

//
export function getValidTagIndex(len: number, currentIndex: number = 0) {
	if (currentIndex <= len && currentIndex > 0) {
		return currentIndex;
	}
	return 0;
}

/**
 * 过滤modules中前十个中的商品模块, 并发出第一个商品池子的请求, 传回UI 模块
 */
interface ReconstructFrontModsProps {
	mods: { [key: string]: TModule };
	meta: FrontMeta;
	metaData: Map<MetadataKeys, MetadataValue>;
	tagQuery: ParseProductTagFromUrlResp;
	runtime: RuntimeProps;
}
export const reconstructFrontMods = async ({
	mods,
	meta,
	metaData,
	tagQuery,
	runtime,
}: ReconstructFrontModsProps) => {
	let nextMods = { ...mods };

	let promises: Promise<PoolForProductResp>[] = [];
	Object.values(mods).forEach((item, i: number) => {
		const itemModule = Object.keys(item.module);
		if (!itemModule || itemModule.length <= 1) {
			delete nextMods[item.id];
		}
		if (item.type === TModuleType.TMultiProductModuleType && i < 10 && item.module.proModules) {
			const proModules: TProductModule[] = item.module.proModules;
			const positionId = item.header.position || item.id;
			const tagIndex =
				tagQuery.modId === positionId
					? heightLightTabIndex(proModules, tagQuery.tagName)
					: 0;
			const firstPool: TProductModule = proModules?.[tagIndex];
			promises.push(
				fetchPoolForProduct({
					item: firstPool,
					meta,
					metaData,
					maxLimit:
						item.layout.interaction === ModuleInteraction.RollOver
							? firstPool?.maxLoad
							: LOAD_LIMIT,
					modId: item.id,
					runtime,
				}),
			);
		}
	});
	const nextPool: PoolForProductResp[] = await Promise.all(promises);
	nextPool.forEach((nextItem) => {
		if (nextMods[nextItem.moduleId]) {
			const nextMod = nextMods[nextItem.moduleId];
			let newProModule: TProductModule[] = nextMod.module.proModules || [];
			const positionId = nextMod.header.position || nextMod.id;
			const tagIndex =
				tagQuery.modId === positionId
					? heightLightTabIndex(newProModule, tagQuery.tagName)
					: 0;

			newProModule[tagIndex] = nextItem.pool;
			nextMods[nextItem.moduleId] = {
				...nextMod,
				module: {
					...nextMod.module,
					proModules: [...newProModule],
				},
			};
		}
	});
	return nextMods;
};
interface FetchPoolForProductReq {
	item: Partial<TProductModule>;
	meta: FrontMeta;
	maxLimit: number;
	metaData: Map<MetadataKeys, MetadataValue>;
	modId: string;
	runtime: RuntimeProps;
}
export const fetchPoolForProduct = async ({
	item,
	meta,
	maxLimit,
	metaData,
	modId,
	runtime,
}: FetchPoolForProductReq): Promise<PoolForProductResp> => {
	const { maxLoad: limit, poolId } = item;
	if (runtime?.appKey) {
		meta = {
			...meta,
			userInfos: {
				app_key: runtime.appKey,
				app_secret: runtime.appSecret,
				sid: runtime.sid,
				sessionkey: runtime.sessionKey,
				sign: transformSign(runtime),
			},
		};
	}
	const pool =
		(await GetProducts(
			{
				poolId,
				offset: "0",
				limit: Math.min(Math.max(maxLimit, 8), limit).toString(),
				meta,
			},
			{ meta: metaData },
		)) || {};

	// 查看server 商品池请求信息
	return {
		moduleId: modId,
		pool: {
			...item,
			productElements: pool?.products || [],
			maxLoad: pool?.hasNext ? limit : pool?.products?.length || 0,
		},
	};
};
/**
 * 刷新模块
 * @param moduleId
 * @param runtime
 * @returns
 */
interface RefreshModuleTransParam {
	runtime: Partial<RuntimeProps>;
}
export const refreshModule = async (
	moduleId: string,
	{ runtime }: Partial<RefreshModuleTransParam>,
): Promise<TModule> => {
	const meta = transformMetaForApi(runtime);
	try {
		const result = await GetFrontModules(
			{
				moduleId: [moduleId],
				meta,
			},
			{
				baseInfo: {
					catalog: runtime.countryCode,
					platform: runtime.platform,
					isuat: isUAT(),
					onlyFetch: true,
				},
			},
		);
		if (result?.mods?.[moduleId]) {
			return result.mods?.[moduleId];
		}
	} catch (e) {}
};
